import { EventEmitter } from '../utils/event-emitter.js';
import { querySelector } from '../utils/helpers.js';
import { SELECTORS, CSS_CLASSES, EVENTS } from '../utils/constants.js';

/**
 * Менеджер модальных окон
 * Отвечает за открытие, закрытие и управление popup окнами
 */
export class PopupManager extends EventEmitter {
  constructor() {
    super();
    this.contactsLink = null;
    this.contactsPopup = null;
    this.closeButton = null;

    this.isOpen = false;
    this.currentPopup = null;
    this.previousFocusElement = null;

    // Для focus trap
    this.focusableElements = [];
    this.firstFocusableElement = null;
    this.lastFocusableElement = null;
    this.eventsInitialized = false;
  }

  /**
   * Инициализация менеджера
   */
  init() {
    this.contactsLink = querySelector(SELECTORS.CONTACTS_LINK);
    this.contactsPopup = querySelector(SELECTORS.CONTACTS_POPUP);
    this.closeButton = querySelector(SELECTORS.CLOSE_POPUP);

    if (!this.contactsLink || !this.contactsPopup || !this.closeButton) {
      console.warn('Элементы popup не найдены');
      return;
    }

    this.setupEventListeners();
    this.setupAccessibility();

    console.log('💬 PopupManager инициализирован');
  }

  /**
   * Настройка обработчиков событий
   */
  setupEventListeners() {
    if (this.eventsInitialized) return;

    // Открытие popup контактов
    this.contactsLink?.addEventListener('click', (event) => {
      event.preventDefault();
      this.openContacts();
    });

    // Закрытие по кнопке
    this.closeButton?.addEventListener('click', () => {
      this.closePopup();
    });

    // Закрытие по клику на overlay
    this.contactsPopup?.addEventListener('click', (event) => {
      if (event.target === this.contactsPopup) {
        this.closePopup();
      }
    });

    // Обработчик клавиатуры
    document.addEventListener('keydown', (event) => {
      this.handleKeyDown(event);
    });

    this.eventsInitialized = true;
  }

  /**
   * Настройка accessibility
   */
  setupAccessibility() {
    if (this.contactsPopup) {
      this.contactsPopup.setAttribute('role', 'dialog');
      this.contactsPopup.setAttribute('aria-modal', 'true');
      this.contactsPopup.setAttribute('aria-labelledby', 'popup-title');
      this.contactsPopup.setAttribute('aria-hidden', 'true');
    }

    if (this.closeButton) {
      this.closeButton.setAttribute('aria-label', 'Закрыть окно контактов');
    }
  }

  /**
   * Обработка нажатий клавиш
   * @param {KeyboardEvent} event - Событие клавиатуры
   */
  handleKeyDown(event) {
    if (!this.isOpen) return;

    switch (event.key) {
    case 'Escape':
      this.closePopup();
      break;

    case 'Tab':
      this.handleTabNavigation(event);
      break;
    }
  }

  /**
   * Обработка Tab навигации для focus trap
   * @param {KeyboardEvent} event - Событие клавиатуры
   */
  handleTabNavigation(event) {
    if (!this.isOpen || this.focusableElements.length === 0) return;

    const isTabPressed = event.key === 'Tab';
    if (!isTabPressed) return;

    // Shift + Tab (назад)
    if (event.shiftKey) {
      if (document.activeElement === this.firstFocusableElement) {
        this.lastFocusableElement.focus();
        event.preventDefault();
      }
    } else {
      // Tab (вперед)
      if (document.activeElement === this.lastFocusableElement) {
        this.firstFocusableElement.focus();
        event.preventDefault();
      }
    }
  }

  /**
   * Открытие popup контактов
   */
  openContacts() {
    if (this.isOpen) return;

    // Сохраняем текущий активный элемент
    this.previousFocusElement = document.activeElement;

    // Устанавливаем текущий popup
    this.currentPopup = this.contactsPopup;

    // Показываем popup
    this.showPopup();

    // Настраиваем focus trap
    this.setupFocusTrap();

    // Устанавливаем фокус на первый элемент
    this.setInitialFocus();

    // Блокируем прокрутку страницы
    this.disableBodyScroll();

    // Обновляем состояние
    this.isOpen = true;

    // Уведомляем об открытии
    this.emit(EVENTS.CONTACTS_OPEN);

    console.log('📞 Popup контактов открыт');
  }

  /**
   * Показ popup с анимацией
   */
  showPopup() {
    if (!this.currentPopup) return;

    // Добавляем класс активности
    this.currentPopup.classList.add(CSS_CLASSES.ACTIVE);

    // Обновляем ARIA атрибуты
    this.currentPopup.setAttribute('aria-hidden', 'false');

    // Анимация появления
    requestAnimationFrame(() => {
      const content = this.currentPopup.querySelector('.popup-content');
      if (content) {
        content.style.transform = 'translateY(0)';
      }
    });
  }

  /**
   * Настройка focus trap
   */
  setupFocusTrap() {
    if (!this.currentPopup) return;

    // Получаем все focusable элементы внутри popup
    const focusableSelectors = [
      'button',
      '[href]',
      'input',
      'select',
      'textarea',
      '[tabindex]:not([tabindex="-1"])',
    ];

    this.focusableElements = Array.from(
      this.currentPopup.querySelectorAll(focusableSelectors.join(', ')),
    ).filter(element => {
      return !element.disabled && !element.hidden && element.offsetWidth > 0 && element.offsetHeight > 0;
    });

    [this.firstFocusableElement] = this.focusableElements;
    this.lastFocusableElement = this.focusableElements[this.focusableElements.length - 1];
  }

  /**
   * Установка начального фокуса
   */
  setInitialFocus() {
    // Фокусируемся на кнопке закрытия
    if (this.closeButton) {
      this.closeButton.focus();
    } else if (this.firstFocusableElement) {
      this.firstFocusableElement.focus();
    }
  }

  /**
   * Блокировка прокрутки страницы
   */
  disableBodyScroll() {
    // Сохраняем текущую позицию прокрутки
    this.scrollPosition = window.pageYOffset;

    // Блокируем прокрутку
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.scrollPosition}px`;
    document.body.style.width = '100%';
  }

  /**
   * Восстановление прокрутки страницы
   */
  enableBodyScroll() {
    // Восстанавливаем прокрутку
    document.body.style.removeProperty('overflow');
    document.body.style.removeProperty('position');
    document.body.style.removeProperty('top');
    document.body.style.removeProperty('width');

    // Восстанавливаем позицию прокрутки
    if (this.scrollPosition !== undefined) {
      window.scrollTo(0, this.scrollPosition);
    }
  }

  /**
   * Закрытие popup
   */
  closePopup() {
    if (!this.isOpen) return;

    // Скрываем popup с анимацией
    this.hidePopup();

    // Восстанавливаем прокрутку
    this.enableBodyScroll();

    // Восстанавливаем фокус
    this.restoreFocus();

    // Обновляем состояние
    this.isOpen = false;
    this.currentPopup = null;
    this.focusableElements = [];
    this.firstFocusableElement = null;
    this.lastFocusableElement = null;

    // Уведомляем о закрытии
    this.emit(EVENTS.CONTACTS_CLOSE);
    
    // Эмитируем событие для активации пункта "Каталог"
    this.emit('setActiveNavigation', 'Каталог');

    console.log('📞 Popup контактов закрыт');
  }

  /**
   * Скрытие popup с анимацией
   */
  hidePopup() {
    if (!this.currentPopup) return;

    // Убираем класс активности
    this.currentPopup.classList.remove(CSS_CLASSES.ACTIVE);

    // Обновляем ARIA атрибуты
    this.currentPopup.setAttribute('aria-hidden', 'true');

    // Сбрасываем анимацию content
    const content = this.currentPopup.querySelector('.popup-content');
    if (content) {
      content.style.transform = 'translateY(-20px)';
    }
  }

  /**
   * Восстановление фокуса
   */
  restoreFocus() {
    if (this.previousFocusElement && this.previousFocusElement.focus) {
      // Небольшая задержка для корректной работы
      setTimeout(() => {
        this.previousFocusElement.focus();
        this.previousFocusElement = null;
      }, 100);
    }
  }

  /**
   * Проверка, открыт ли popup
   * @returns {boolean} - Состояние popup
   */
  isPopupOpen() {
    return this.isOpen;
  }

  /**
   * Получение текущего открытого popup
   * @returns {Element|null} - Текущий popup элемент
   */
  getCurrentPopup() {
    return this.currentPopup;
  }

  /**
   * Принудительное закрытие всех popup
   */
  closeAllPopups() {
    if (this.isOpen) {
      this.closePopup();
    }
  }

  /**
   * Создание нового popup программно
   * @param {Object} config - Конфигурация popup
   * @returns {Element} - Созданный popup элемент
   */
  createPopup(config) {
    const { title, content, className = '', showCloseButton = true } = config;

    // Создаем overlay
    const overlay = document.createElement('div');
    overlay.className = `popup-overlay ${className}`;
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-hidden', 'true');

    // Создаем content
    const popupContent = document.createElement('div');
    popupContent.className = 'popup-content';

    // Заголовок
    if (title) {
      const header = document.createElement('div');
      header.className = 'popup-header';

      const titleElement = document.createElement('div');
      titleElement.className = 'popup-title';
      titleElement.textContent = title;
      titleElement.id = 'popup-title';
      header.appendChild(titleElement);

      if (showCloseButton) {
        const closeBtn = document.createElement('button');
        closeBtn.className = 'close-popup';
        closeBtn.innerHTML = '×';
        closeBtn.setAttribute('aria-label', 'Закрыть');
        header.appendChild(closeBtn);

        // Обработчик закрытия
        closeBtn.addEventListener('click', () => {
          this.closeCustomPopup(overlay);
        });
      }

      popupContent.appendChild(header);
    }

    // Тело popup
    if (content) {
      const body = document.createElement('div');
      body.className = 'popup-body';

      if (typeof content === 'string') {
        body.innerHTML = content;
      } else if (content instanceof Element) {
        body.appendChild(content);
      }

      popupContent.appendChild(body);
    }

    overlay.appendChild(popupContent);

    // Обработчик закрытия по клику на overlay
    overlay.addEventListener('click', (event) => {
      if (event.target === overlay) {
        this.closeCustomPopup(overlay);
      }
    });

    return overlay;
  }

  /**
   * Показ пользовательского popup
   * @param {Element} popup - Элемент popup
   */
  showCustomPopup(popup) {
    if (this.isOpen) {
      this.closePopup();
    }

    document.body.appendChild(popup);

    this.currentPopup = popup;
    this.previousFocusElement = document.activeElement;

    this.showPopup();
    this.setupFocusTrap();
    this.setInitialFocus();
    this.disableBodyScroll();

    this.isOpen = true;
  }

  /**
   * Закрытие пользовательского popup
   * @param {Element} popup - Элемент popup
   */
  closeCustomPopup(popup) {
    popup.classList.remove(CSS_CLASSES.ACTIVE);

    setTimeout(() => {
      if (popup.parentNode) {
        popup.parentNode.removeChild(popup);
      }
    }, 300);

    if (this.currentPopup === popup) {
      this.enableBodyScroll();
      this.restoreFocus();
      this.isOpen = false;
      this.currentPopup = null;
    }
  }

  /**
   * Получение статистики popup
   * @returns {Object} - Статистика
   */
  getStatistics() {
    return {
      isOpen: this.isOpen,
      currentPopup: this.currentPopup?.className || null,
      focusableElementsCount: this.focusableElements.length,
      hasScrollLock: document.body.style.overflow === 'hidden',
    };
  }

  /**
   * Деструктор
   */
  destroy() {
    // Закрываем открытые popup
    this.closeAllPopups();

    // Восстанавливаем прокрутку на всякий случай
    this.enableBodyScroll();

    this.removeAllListeners();

    this.contactsLink = null;
    this.contactsPopup = null;
    this.closeButton = null;
    this.currentPopup = null;
    this.previousFocusElement = null;
    this.focusableElements = [];
  }
}
